import clsx from "clsx";
import type {
  ChangeEvent,
  FC,
  HTMLAttributes,
  ReactNode,
  Ref,
  TextareaHTMLAttributes,
} from "react";
import "./LegacyTextArea.scss";
import TextareaAutosize, { type TextareaAutosizeProps } from "react-textarea-autosize";
import { twMerge } from "tailwind-merge";

import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import {
  ComponentEventPropertiesWithKey,
  USER_INPUT_EVENT_CATEGORY,
  addTrackingToEvent,
} from "utils/amplitude";
import { useLocalCopy } from "utils/collections";

type LegacyTextAreaType = TextareaAutosizeProps & TextareaHTMLAttributes<HTMLTextAreaElement>;
interface LegacyTextAreaProps extends LegacyTextAreaType, ComponentEventPropertiesWithKey {
  children?: ReactNode;
  ref?: Ref<HTMLTextAreaElement>;
  size?: "xs" | "s" | "m";
  divProps?: HTMLAttributes<HTMLDivElement>;
}

// TODO: Add a ResizeObserver so that auto sizing will work when we open/close the sidebar while viewing rubrics.
/** For single field text area features (e.g. AIChatPanel, CharacterCounter). */
export const LegacyTextArea: FC<LegacyTextAreaProps> = ({
  size,
  className = "",
  children,
  value: originalValue,
  eventName,
  eventProperties: eventProps,
  eventPropertyKey,
  ...props
}) => {
  // If the original value changes from a different source while we are editing, it's
  // better to reflect that immediately, so the user can react and doesn't overwrite someone
  // else's changes.
  const [value, setValue] = useLocalCopy(originalValue);

  const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    let eventProperties = { ...eventProps, ...USER_INPUT_EVENT_CATEGORY };

    if (eventPropertyKey) {
      eventProperties = { ...eventProperties, [eventPropertyKey]: event.target.value };
    }

    setValue(event.target.value);

    addTrackingToEvent<ChangeEvent<HTMLTextAreaElement>>({
      eventName,
      eventProperties,
      onEvent: props.onChange,
    })(event);
  };

  return (
    <BaseTextArea className={className} size={size} value={value} onChange={onChange} {...props}>
      {children}
    </BaseTextArea>
  );
};

LegacyTextArea.displayName = "LegacyTextArea";

/**
 * For multi-field text area features that rely on their own hooks (e.g. RubricList) to work rather than
 * rely on useLocalCopy from TextArea, which will cause rerender issues. */
export const BaseTextArea: FC<LegacyTextAreaProps> = ({
  size,
  className = "",
  divProps = {},
  children,
  ...props
}) => {
  // On mobile devices browsers automatically zoom when input font-size is < 16px
  // This is a workaround to ensure the page doesn't zoom in on mobile devices
  // https://wsform.com/knowledgebase/why-forms-zoom-on-some-mobile-devices-and-browsers-and-how-to-control-it/#:~:text=Mobile%20browsers%20typically%20zoom%20in,using%20your%20own%20custom%20CSS.
  const isMdOrLarger = useIsMdOrLarger();
  const { readOnly, value } = props;
  const mobileFriendlySizeClass = readOnly || isMdOrLarger ? `body-${size}` : "body-m";
  const textAreaClass = clsx("text-area", mobileFriendlySizeClass, className);

  // We use a div instead of a textarea when the text area is read only so that
  // we can minimize resizing and scrolling issues when the space is constrained
  // or changed. We've run into issues where TextAreaAutosize may leave small
  // scrollbars or doesn't resize on different browsers or devices.
  return readOnly ? (
    <div
      {...divProps}
      className={twMerge(
        "whitespace-pre-wrap rounded-lg border border-transparent pl-1.5",
        mobileFriendlySizeClass,
        divProps.className,
        className,
      )}
    >
      {value}
    </div>
  ) : (
    <TextareaAutosize className={textAreaClass} {...props}>
      {children}
    </TextareaAutosize>
  );
};

BaseTextArea.displayName = "BaseTextArea";
